import { Order, isOrderExpired } from '@velocity-exchange/sdk';
import { UISerializableOrder } from '@velocity-exchange/common';

/**
 * Seconds past an order's on-chain `maxTs` before the UI treats it as expired and
 * hides it. Deliberately distinct from `Env.orderMaxTsBufferMs` (7s), which governs
 * the market-order progress toast — this is the client-side hide/expiry grace period.
 */
export const ORDER_EXPIRY_HIDE_BUFFER_SECONDS = 5;

/**
 * Mirrors the on-chain `should_expire_order` (velocity program `math/orders.rs`): an
 * order can no longer be filled once `now > maxTs`. Orders with no expiry
 * (`maxTs == 0`) and trigger orders are never expired, exactly like the program.
 *
 * The SDK's `isOrderExpired` returns `new BN(ts).gt(maxTs)`, so evaluating it at
 * `chainTs - bufferSeconds` yields `chainTs > maxTs + bufferSeconds` — a uniform grace
 * period that also applies to market orders (the SDK's own `enforceBuffer` only buffers
 * limit orders, which is why we shift the timestamp instead).
 *
 * `chainTs` must be the on-chain clock timestamp (unix seconds), not `Date.now()`, so
 * the UI agrees with the program's notion of `now`. `UISerializableOrder` is
 * structurally compatible with the SDK `Order` the function expects (BN `maxTs`,
 * variant `status`/`orderType`).
 */
export function isUiOrderExpired(
	order: UISerializableOrder,
	chainTs: number,
	bufferSeconds: number = ORDER_EXPIRY_HIDE_BUFFER_SECONDS
): boolean {
	return isOrderExpired(order as unknown as Order, chainTs - bufferSeconds);
}

/**
 * Returns the `orderId`s of the given orders that are expired as of `chainTs`. Returns
 * an empty set when the chain clock has not been observed yet (fail safe: hide nothing).
 */
export function getExpiredOrderIds(
	orders: UISerializableOrder[],
	chainTs: number | undefined,
	bufferSeconds: number = ORDER_EXPIRY_HIDE_BUFFER_SECONDS
): Set<number> {
	const expiredIds = new Set<number>();
	if (!chainTs) return expiredIds;
	for (const order of orders) {
		if (isUiOrderExpired(order, chainTs, bufferSeconds)) {
			expiredIds.add(order.orderId);
		}
	}
	return expiredIds;
}
